/* eslint-disable camelcase */
import FuseUtils from '@fuse/utils/FuseUtils';
import jwtDecode from 'jwt-decode';
import axios from 'axios';

import coreAPI from 'apis/core';
import { clearLocalStorageExceptSeen } from 'app/utils/seenStorageKeys';
import { axiosPrivate, axiosPublic } from '../../utils';

class JwtService extends FuseUtils.EventEmitter {
  init() {
    if (this.initialized) return;

    this.initialized = true;
    this.refreshTokenPromise = null;
    this.setInterceptors();
    this.handleAuthentication();
  }

  setInterceptors = () => {
    axiosPrivate.interceptors.request.use(
      async (request) => {
        let accessToken = this.getAccessToken();

        if (this.isTokenValid(accessToken)) {
          // Token is valid, set headers and return request
          this.setHeaders(request, accessToken);
          return request;
        }

        // No valid access token and no refresh token means there is no active session
        // (e.g. just after logout). Cancel the request instead of firing it without
        // credentials — with SPA navigation, residual calls would otherwise hit the API
        // as `Bearer null` and 401.
        if (!this.getRefreshToken()) {
          return Promise.reject(new axios.Cancel('No active session'));
        }

        // First to get here initiate token refresh
        if (!this.refreshTokenPromise) {
          this.refreshTokenPromise = this.handleRefreshToken(request);
          await this.refreshTokenPromise;
        }

        // All other wait for the refresh to complete
        if (this.refreshTokenPromise) {
          await this.refreshTokenPromise;
        }

        accessToken = this.getAccessToken();
        if (!accessToken) {
          // Refresh failed (handleRefreshToken logged the user out and cleared the tokens).
          // Don't resume the request with `Bearer null` — cancel it. refreshTokenPromise was
          // already reset, so a later request can attempt a fresh refresh.
          return Promise.reject(new axios.Cancel('No active session'));
        }

        this.setHeaders(request, accessToken);
        return request;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axiosPrivate.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && error.config && !error.config.__isRetryRequest) {
          // if you ever get an unauthorized response, logout the user
          this.emit('onAutoLogout', 'Invalid access_token');
          this.setTokens({ accessToken: null, refreshToken: null });
        }

        throw error;
      }
    );
  };

  setHeaders = (request, accessToken) => {
    request.headers.Authorization = `Bearer ${accessToken}`;
    request.headers.token = accessToken;
  };

  handleAuthentication = () => {
    const refresh_token = this.getRefreshToken();

    // TODO Remove when Connect v1 is deprecated
    const queryParams = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });

    if (queryParams.logout === 'true') {
      // Forced logout handed off from connect-v1: clear the session in place and stay
      // on the login page (no auto-login, no redirect to home).
      this.clearSession();
      window.history.replaceState({}, document.title, window.location.pathname);
      this.emit('onNoAccessToken');
      return;
    }

    if (queryParams.access_id) {
      // This is redirect from Connect V1, exchange the one-time access_id for a session
      this.emit('onAutoLogin', queryParams.access_id);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    const nonExpiryAccessToken = this.getAccessToken();
    if (nonExpiryAccessToken) {
      this.emit('onAutoLogin');
      return;
    }
    // Remove end

    if (!refresh_token) {
      this.emit('onNoAccessToken');
      return;
    }

    if (this.isTokenValid(refresh_token, 'refresh')) {
      this.emit('onAutoLogin');
    } else {
      this.setTokens({ refreshToken: null });
      this.emit('onAutoLogout', 'refresh_token expired');
    }
  };

  createUser = async (data) => {
    const response = await coreAPI.auth.createUser(data);
    this.setTokens({ accessToken: response.access_token });
    return response.user;
  };

  signInWithEmailAndPassword = async (username, password) => {
    const response = await coreAPI.auth.login({
      username,
      user_password: password,
    });

    this.setTokens({ accessToken: response.token, refreshToken: response.refresh_token });
    return response.user;
  };

  signInWithToken = async () => {
    const accessToken = this.getAccessToken();

    if (accessToken) {
      const decodedToken = jwtDecode(accessToken);
      const tokenExpiryEnabled = Boolean(decodedToken.sub);

      if (!tokenExpiryEnabled) {
        this.setTokens({ accessToken });
        const response = await coreAPI.user.getUserById(decodedToken.user_id);

        return response.user;
      }
    }

    const refreshToken = this.getRefreshToken();

    if (refreshToken) {
      this.refreshingToken =
        this.refreshingToken || coreAPI.auth.login({ refresh_token: refreshToken });

      const response = await this.refreshingToken;
      setTimeout(() => {
        this.refreshingToken = null;
      }, 1000);

      // The session may have been cleared (e.g. the user logged out) while this refresh
      // was in flight. Don't write the tokens back and resurrect the session.
      if (!this.getRefreshToken()) return null;

      this.setTokens({ accessToken: response.token, refreshToken: response.refresh_token });
      return response.user;
    }

    return null;
  };

  signInWithAccess = async (accessId) => {
    // TODO Remove after Connect V1 is deprecated
    // The access_id is single-use: the API consumes it on the first exchange.
    const response = await axiosPublic.get('/access', { params: { access_id: accessId } });
    const session = response.data?.data?.[0];
    if (!session) throw new Error('Response malformed');

    this.setTokens({ accessToken: session.token, refreshToken: session.refresh_token });
    localStorage.setItem('selected_pharmacy_id', session.pharmacy_id);
    return session.user;
  };

  handleRefreshToken = async () => {
    try {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) {
        this.logout();
        return;
      }

      const response = await axiosPublic.post('/tokens', {
        refresh_token: refreshToken,
      });

      // The session may have been cleared (e.g. the user logged out) while this refresh
      // was in flight. Don't write the rotated tokens back and resurrect the session.
      if (!this.getRefreshToken()) return;

      const { token: newAccessToken, refresh_token: newRefreshToken } =
        response.data?.data?.[0] || {};

      this.setTokens({ accessToken: newAccessToken, refreshToken: newRefreshToken });
    } catch (error) {
      this.logout();
    } finally {
      this.refreshTokenPromise = null;
    }
  };

  setTokens = ({ accessToken, refreshToken }) => {
    if (accessToken !== undefined) {
      if (accessToken) localStorage.setItem('jwt_access_token', accessToken);
      if (!accessToken) localStorage.removeItem('jwt_access_token');
    }

    if (refreshToken !== undefined) {
      if (refreshToken) localStorage.setItem('jwt_refresh_token', refreshToken);
      if (!refreshToken) localStorage.removeItem('jwt_refresh_token');
    }
  };

  clearSession = () => {
    // Keeps what the user has already seen, so pop-ups shown once don't come back on every login.
    clearLocalStorageExceptSeen();
    // Drop any in-flight refresh so its resolution can't write tokens back after logout.
    this.refreshTokenPromise = null;
    this.refreshingToken = null;
  };

  // Revoke the session server-side so the refresh token can't be replayed after logout.
  // Best-effort (swallows errors) and must run while the access token is still in storage,
  // i.e. before clearSession(). axiosPrivate attaches the current bearer token.
  revokeSession = () => axiosPrivate.delete('/tokens', { timeout: 5000 }).catch(() => undefined);

  logout = () => {
    // Force a logout without a full browser reload: clear the session and let the
    // redux-aware listener (Auth.js -> logoutUser) navigate to /login via the router.
    this.clearSession();
    this.emit('onAutoLogout');
  };

  isTokenValid = (token, tokenType = 'access') => {
    if (!token) {
      return false;
    }

    const decoded = jwtDecode(token);

    // TODO: Remove when Connect v1 is deprecated or when we have green lite of logging out all pharmacists with prehistoric tokens
    const isOldTokenThatDoesNotExpire = tokenType === 'access' && !decoded.sub;
    if (isOldTokenThatDoesNotExpire) return true;

    const currentTime = Date.now() / 1000 + 60; // Add 60 seconds (1 minute) to the current time
    if (decoded.exp < currentTime) {
      return false;
    }

    return true;
  };

  getAccessToken = () => {
    return localStorage.getItem('jwt_access_token');
  };

  getRefreshToken = () => {
    return localStorage.getItem('jwt_refresh_token');
  };
}

const instance = new JwtService();

export default instance;
